import React, { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import PageNavbar from "components/PageNavbar";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import Card from "@mui/material/Card";
import { Grid, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, IconButton, Tabs, Tab } from "@mui/material";
import { IoEyeOutline, IoPeopleOutline, IoSearchOutline, IoCloseOutline, IoBusiness, IoConstruct, IoCart } from "react-icons/io5";
import axios from "axios";
import { API_URL } from "../../config/api";

function UserSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  useEffect(() => {
    filterSubmissions();
  }, [submissions, filterType, searchQuery]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/submissions`);
      if (response.data.success) {
        setSubmissions(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching submissions:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterSubmissions = () => {
    let filtered = submissions;

    // Filter by type
    if (filterType !== "all") {
      filtered = filtered.filter(sub => sub.userType === filterType);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(sub => {
        const searchLower = searchQuery.toLowerCase();
        return (
          sub.data.companyName?.toLowerCase().includes(searchLower) ||
          sub.data.contactPerson?.toLowerCase().includes(searchLower) ||
          sub.data.email?.toLowerCase().includes(searchLower) ||
          sub.userType.toLowerCase().includes(searchLower)
        );
      });
    }

    setFilteredSubmissions(filtered);
  };

  const handleViewDetails = (submission) => {
    setSelectedSubmission(submission);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSubmission(null);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "supplier":
        return <IoCart size="24px" color="white" />;
      case "contractor":
        return <IoConstruct size="24px" color="white" />;
      case "constructor":
        return <IoBusiness size="24px" color="white" />;
      default:
        return <IoPeopleOutline size="24px" color="white" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "supplier":
        return "linear-gradient(97.89deg, #0075ff, #0075ff)";
      case "contractor":
        return "linear-gradient(97.89deg, #00bfa5, #00897b)";
      case "constructor":
        return "linear-gradient(97.89deg, #ff6b6b, #ee5a6f)";
      default:
        return "linear-gradient(97.89deg, #0075ff, #0075ff)";
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const SubmissionCard = ({ submission }) => (
    <Card
      sx={{
        background: "linear-gradient(127.09deg, rgba(6, 11, 40, 0.94) 19.41%, rgba(10, 14, 35, 0.49) 76.65%)",
        backdropFilter: "blur(120px)",
        border: "2px solid rgba(255, 255, 255, 0.125)",
        borderRadius: "20px",
        padding: "20px",
        height: "100%",
        cursor: "pointer",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-5px)",
          border: "2px solid rgba(67, 24, 255, 0.5)",
        }
      }}
      onClick={() => handleViewDetails(submission)}
    >
      <VuiBox display="flex" alignItems="center" mb={2}>
        <VuiBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            background: getTypeColor(submission.userType),
            width: "50px",
            height: "50px",
            borderRadius: "12px",
            marginRight: "15px",
          }}
        >
          {getTypeIcon(submission.userType)}
        </VuiBox>
        <VuiBox flex={1}>
          <VuiTypography variant="h6" color="white" fontWeight="bold">
            {submission.data.companyName}
          </VuiTypography>
          <Chip
            label={submission.userType.toUpperCase()}
            size="small"
            sx={{
              background: getTypeColor(submission.userType),
              color: "white",
              fontSize: "10px",
              fontWeight: "bold",
              height: "20px"
            }}
          />
        </VuiBox>
      </VuiBox>

      <VuiBox mb={2}>
        <VuiTypography variant="caption" color="text" fontWeight="regular">
          <strong style={{ color: "white" }}>Contact:</strong> {submission.data.contactPerson}
        </VuiTypography>
        <VuiTypography variant="caption" color="text" fontWeight="regular" display="block">
          <strong style={{ color: "white" }}>Email:</strong> {submission.data.email}
        </VuiTypography>
        <VuiTypography variant="caption" color="text" fontWeight="regular" display="block">
          <strong style={{ color: "white" }}>Phone:</strong> {submission.data.phone}
        </VuiTypography>
      </VuiBox>

      <VuiBox display="flex" justifyContent="space-between" alignItems="center">
        <VuiTypography variant="caption" color="text" fontWeight="regular">
          {formatDate(submission.submittedAt)}
        </VuiTypography>
        <IconButton
          sx={{
            background: "rgba(67, 24, 255, 0.2)",
            color: "#0075ff",
            "&:hover": {
              background: "rgba(67, 24, 255, 0.3)"
            }
          }}
        >
          <IoEyeOutline size="20px" />
        </IconButton>
      </VuiBox>
    </Card>
  );

  return (
    <DashboardLayout>
      <PageNavbar />
      <VuiBox py={3}>
        {/* Header */}
        <VuiBox mb={3}>
          <Card
            sx={{
              background: "linear-gradient(127.09deg, rgba(6, 11, 40, 0.94) 19.41%, rgba(10, 14, 35, 0.49) 76.65%)",
              backdropFilter: "blur(120px)",
              border: "2px solid rgba(255, 255, 255, 0.125)",
              borderRadius: "20px",
              padding: "25px",
            }}
          >
            <VuiBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <VuiBox display="flex" alignItems="center">
                <VuiBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    background: "linear-gradient(97.89deg, #0075ff, #0075ff)",
                    width: "60px",
                    height: "60px",
                    borderRadius: "12px",
                    marginRight: "20px",
                  }}
                >
                  <IoPeopleOutline size="30px" color="white" />
                </VuiBox>
                <VuiBox>
                  <VuiTypography variant="h3" color="white" fontWeight="bold">
                    User Submissions
                  </VuiTypography>
                  <VuiTypography variant="button" color="text" fontWeight="regular">
                    {filteredSubmissions.length} {filterType === "all" ? "Total" : filterType} Submissions
                  </VuiTypography>
                </VuiBox>
              </VuiBox>

              {/* Search Bar */}
              <VuiBox sx={{ minWidth: "300px" }}>
                <VuiInput
                  placeholder="Search by name, email, or type..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  icon={{ component: <IoSearchOutline size="18px" />, direction: "left" }}
                  sx={{
                    background: "rgba(0, 0, 0, 0.3)",
                    borderRadius: "12px"
                  }}
                />
              </VuiBox>
            </VuiBox>

            {/* Filter Tabs */}
            <VuiBox mt={3}>
              <Tabs
                value={filterType}
                onChange={(e, newValue) => setFilterType(newValue)}
                sx={{
                  "& .MuiTabs-indicator": {
                    display: "none"
                  },
                  "& .MuiTabs-flexContainer": {
                    gap: "12px"
                  },
                  "& .MuiTabs-root": {
                    background: "transparent"
                  },
                  background: "transparent"
                }}
              >
                <Tab
                  value="all"
                  label={`All (${submissions.length})`}
                  sx={{
                    color: "rgba(255, 255, 255, 0.5)",
                    fontWeight: "bold",
                    fontSize: "14px",
                    minHeight: "45px",
                    padding: "10px 24px",
                    borderRadius: "12px",
                    background: "transparent",
                    border: "2px solid rgba(255, 255, 255, 0.2)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "rgba(255, 255, 255, 0.05)",
                      color: "rgba(255, 255, 255, 0.8)",
                      border: "2px solid rgba(255, 255, 255, 0.3)"
                    },
                    "&.Mui-selected": {
                      color: "#fff",
                      background: "rgba(0, 117, 255, 0.2)",
                      border: "2px solid #0075ff",
                      boxShadow: "0 0 20px rgba(0, 117, 255, 0.3)"
                    }
                  }}
                />
                <Tab
                  value="supplier"
                  label={`Suppliers (${submissions.filter(s => s.userType === "supplier").length})`}
                  sx={{
                    color: "rgba(255, 255, 255, 0.5)",
                    fontWeight: "bold",
                    fontSize: "14px",
                    minHeight: "45px",
                    padding: "10px 24px",
                    borderRadius: "12px",
                    background: "transparent",
                    border: "2px solid rgba(255, 255, 255, 0.2)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "rgba(255, 255, 255, 0.05)",
                      color: "rgba(255, 255, 255, 0.8)",
                      border: "2px solid rgba(255, 255, 255, 0.3)"
                    },
                    "&.Mui-selected": {
                      color: "#fff",
                      background: "rgba(0, 117, 255, 0.2)",
                      border: "2px solid #0075ff",
                      boxShadow: "0 0 20px rgba(0, 117, 255, 0.3)"
                    }
                  }}
                />
                <Tab
                  value="contractor"
                  label={`Contractors (${submissions.filter(s => s.userType === "contractor").length})`}
                  sx={{
                    color: "rgba(255, 255, 255, 0.5)",
                    fontWeight: "bold",
                    fontSize: "14px",
                    minHeight: "45px",
                    padding: "10px 24px",
                    borderRadius: "12px",
                    background: "transparent",
                    border: "2px solid rgba(255, 255, 255, 0.2)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "rgba(255, 255, 255, 0.05)",
                      color: "rgba(255, 255, 255, 0.8)",
                      border: "2px solid rgba(255, 255, 255, 0.3)"
                    },
                    "&.Mui-selected": {
                      color: "#fff",
                      background: "rgba(0, 117, 255, 0.2)",
                      border: "2px solid #0075ff",
                      boxShadow: "0 0 20px rgba(0, 117, 255, 0.3)"
                    }
                  }}
                />
                <Tab
                  value="constructor"
                  label={`Constructors (${submissions.filter(s => s.userType === "constructor").length})`}
                  sx={{
                    color: "rgba(255, 255, 255, 0.5)",
                    fontWeight: "bold",
                    fontSize: "14px",
                    minHeight: "45px",
                    padding: "10px 24px",
                    borderRadius: "12px",
                    background: "transparent",
                    border: "2px solid rgba(255, 255, 255, 0.2)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "rgba(255, 255, 255, 0.05)",
                      color: "rgba(255, 255, 255, 0.8)",
                      border: "2px solid rgba(255, 255, 255, 0.3)"
                    },
                    "&.Mui-selected": {
                      color: "#fff",
                      background: "rgba(0, 117, 255, 0.2)",
                      border: "2px solid #0075ff",
                      boxShadow: "0 0 20px rgba(0, 117, 255, 0.3)"
                    }
                  }}
                />
              </Tabs>
            </VuiBox>
          </Card>
        </VuiBox>

        {/* Submissions Grid */}
        {loading ? (
          <VuiBox textAlign="center" py={5}>
            <CircularProgress sx={{ color: "#0075ff" }} />
          </VuiBox>
        ) : filteredSubmissions.length === 0 ? (
          <Card
            sx={{
              background: "linear-gradient(127.09deg, rgba(6, 11, 40, 0.94) 19.41%, rgba(10, 14, 35, 0.49) 76.65%)",
              backdropFilter: "blur(120px)",
              border: "2px solid rgba(255, 255, 255, 0.125)",
              borderRadius: "20px",
              padding: "50px",
              textAlign: "center"
            }}
          >
            <VuiTypography variant="h5" color="text" fontWeight="regular">
              No submissions found
            </VuiTypography>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {filteredSubmissions.map((submission) => (
              <Grid item xs={12} md={6} lg={4} key={submission.id}>
                <SubmissionCard submission={submission} />
              </Grid>
            ))}
          </Grid>
        )}
      </VuiBox>

      {/* Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: "linear-gradient(127.09deg, rgba(6, 11, 40, 0.98) 19.41%, rgba(10, 14, 35, 0.98) 76.65%)",
            backdropFilter: "blur(120px)",
            border: "2px solid rgba(255, 255, 255, 0.125)",
            borderRadius: "20px"
          }
        }}
      >
        {selectedSubmission && (
          <>
            <DialogTitle>
              <VuiBox display="flex" alignItems="center" justifyContent="space-between">
                <VuiBox display="flex" alignItems="center">
                  <VuiBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      background: getTypeColor(selectedSubmission.userType),
                      width: "50px",
                      height: "50px",
                      borderRadius: "12px",
                      marginRight: "15px",
                    }}
                  >
                    {getTypeIcon(selectedSubmission.userType)}
                  </VuiBox>
                  <VuiBox>
                    <VuiTypography variant="h5" color="white" fontWeight="bold">
                      {selectedSubmission.data.companyName}
                    </VuiTypography>
                    <Chip
                      label={selectedSubmission.userType.toUpperCase()}
                      size="small"
                      sx={{
                        background: getTypeColor(selectedSubmission.userType),
                        color: "white",
                        fontSize: "10px",
                        fontWeight: "bold",
                        height: "20px"
                      }}
                    />
                  </VuiBox>
                </VuiBox>
                <IconButton onClick={handleCloseDialog}>
                  <IoCloseOutline size="24px" color="white" />
                </IconButton>
              </VuiBox>
            </DialogTitle>
            <DialogContent>
              <VuiBox>
                <Grid container spacing={2}>
                  {Object.entries(selectedSubmission.data).map(([key, value]) => (
                    <Grid item xs={12} md={6} key={key}>
                      <VuiBox
                        sx={{
                          background: "rgba(255, 255, 255, 0.05)",
                          borderRadius: "10px",
                          padding: "15px",
                          border: "1px solid rgba(255, 255, 255, 0.1)"
                        }}
                      >
                        <VuiTypography variant="caption" color="text" fontWeight="bold" display="block" mb={0.5}>
                          {key.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase())}
                        </VuiTypography>
                        <VuiTypography variant="body2" color="white" fontWeight="regular">
                          {value || "N/A"}
                        </VuiTypography>
                      </VuiBox>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <VuiBox
                      sx={{
                        background: "rgba(67, 24, 255, 0.1)",
                        borderRadius: "10px",
                        padding: "15px",
                        border: "1px solid rgba(67, 24, 255, 0.3)"
                      }}
                    >
                      <VuiTypography variant="caption" color="text" fontWeight="bold" display="block" mb={0.5}>
                        Submitted At
                      </VuiTypography>
                      <VuiTypography variant="body2" color="white" fontWeight="regular">
                        {formatDate(selectedSubmission.submittedAt)}
                      </VuiTypography>
                    </VuiBox>
                  </Grid>
                </Grid>
              </VuiBox>
            </DialogContent>
            <DialogActions sx={{ padding: "20px" }}>
              <Button
                onClick={handleCloseDialog}
                sx={{
                  background: "linear-gradient(97.89deg, #0075ff, #0075ff)",
                  color: "white",
                  padding: "10px 30px",
                  borderRadius: "10px",
                  fontWeight: "bold",
                  "&:hover": {
                    background: "linear-gradient(97.89deg, #0055cc, #0065dd)"
                  }
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default UserSubmissions;

